import {
    OnMaskChange,
    OnPermissionDelete,
    PermissionDefinition,
} from './permissionsTypes.ts';
import {Ace, UserType} from '../../types';
import {useTranslation} from 'react-i18next';
import {Box} from '@mui/material';
import PermissionRow from './PermissionRow';
import type {TFunction} from '@alchemy/i18n';
import PermissionRowSkeleton from './PermissionRowSkeleton';

type Props = {
    permissions: Ace[] | undefined;
    onMaskChange: OnMaskChange;
    onDelete: OnPermissionDelete;
    definitions: PermissionDefinition[];
    hasAll: boolean;
};

export default function PermissionTable({
    permissions,
    onMaskChange,
    onDelete,
    definitions,
    hasAll,
}: Props) {
    const {t} = useTranslation();
    const selectSize = 42;
    const actionsSize = 150;

    const columnCount = definitions.length + (hasAll ? 1 : 0);

    return (
        <Box
            component={'table'}
            className={'acl-table'}
            sx={theme => ({
                'border': 'none',
                'width': '100%',
                'borderCollapse': 'separate',
                'borderSpacing': 0,
                '.ug, .a': {
                    p: 1,
                },
                '.p': {
                    'width': selectSize,
                    'maxWidth': selectSize,
                    'fontWeight': 400,
                    'textAlign': 'center',
                    '&:nth-of-type(even)': {
                        backgroundColor: theme.palette.grey[100],
                        ...theme.applyStyles('dark', {
                            backgroundColor: theme.palette.grey[700],
                        }),
                    },
                },
                'td': {
                    'verticalAlign': 'middle',
                    '.p': {
                        textAlign: 'center',
                    },
                    '&.empty': {
                        textAlign: 'center',
                        fontStyle: 'italic',
                        p: 2,
                    },
                },
                '.a': {
                    width: actionsSize,
                    maxWidth: actionsSize,
                },
                'th': {
                    'textAlign': 'left',
                    'verticalAlign': 'baseline',
                    '&.p': {
                        p: 1,
                        span: {
                            writingMode: 'vertical-rl',
                            margin: '0 auto',
                        },
                    },
                },
                '.wc': {
                    td: {
                        opacity: 0.6,
                    },
                },
            })}
        >
            <thead>
                <tr>
                    <th className={'ug'}>
                        {t('acl.table.cols.user_group', `User/Group`)}
                    </th>
                    {definitions.map(def => {
                        return (
                            <th key={def.key} className={'p'}>
                                <span>{def.label}</span>
                            </th>
                        );
                    })}
                    {hasAll ? (
                        <th className={'p'}>
                            <span>{t('acl.permission.all', 'All')}</span>
                        </th>
                    ) : null}
                    <th className={'a'}>
                        {t('permission_table.actions', `Actions`)}
                    </th>
                </tr>
            </thead>
            <tbody>
                {!permissions ? (
                    [0, 1, 2].map(k => (
                        <PermissionRowSkeleton
                            columnCount={columnCount}
                            key={k}
                        />
                    ))
                ) : permissions.length > 0 ? (
                    permissions.map(p => (
                        <PermissionRow
                            {...p}
                            hasAll={hasAll}
                            definitions={definitions}
                            onMaskChange={onMaskChange}
                            onDelete={onDelete}
                            userName={getUserName(p, t)}
                            key={p.id || `${p.userId}::${p.userType}`}
                        />
                    ))
                ) : (
                    <tr>
                        <td colSpan={columnCount + 2} className={'empty'}>
                            {t(
                                'permission_table.no_permissions',
                                `No permissions defined`
                            )}
                        </td>
                    </tr>
                )}
            </tbody>
        </Box>
    );
}

function getUserName(p: Ace, t: TFunction): string | undefined {
    const userId = p.userId;

    if (p.userType === UserType.User) {
        if (userId) {
            return (
                p.user?.username ??
                t('get_user_name.user_not_found', `User not found`)
            );
        }

        return t('get_user_name.all_users', `All users`);
    } else if (p.userType === UserType.Group) {
        if (userId) {
            return (
                p.group?.name ??
                t('get_user_name.group_not_found', `Group not found`)
            );
        }

        return t('get_user_name.all_groups', `All groups`);
    }
}
